import { authAPI } from './api';

// Auth Service
class AuthService {
  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private user: any = null;
  private permissions: string[] = [];

  constructor() {
    this.loadFromStorage();
  }

  // Load tokens and user from localStorage
  private loadFromStorage() {
    this.accessToken = localStorage.getItem('accessToken');
    this.refreshToken = localStorage.getItem('refreshToken');
    const userStr = localStorage.getItem('user');
    const permissionsStr = localStorage.getItem('permissions');
    
    if (userStr) {
      try {
        this.user = JSON.parse(userStr);
      } catch (error) {
        console.error('Error parsing user from storage:', error);
        this.clearStorage();
      }
    }
    
    if (permissionsStr) {
      try {
        this.permissions = JSON.parse(permissionsStr);
      } catch (error) {
        console.error('Error parsing permissions from storage:', error);
        this.permissions = [];
      }
    }
  }

  // Save tokens and user to localStorage
  private saveToStorage(accessToken: string, refreshToken: string, user: any, permissions: string[]) {
    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('token', accessToken);
    localStorage.setItem('refreshToken', refreshToken);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('permissions', JSON.stringify(permissions));
    
    this.accessToken = accessToken;
    this.refreshToken = refreshToken;
    this.user = user;
    this.permissions = permissions;
  }

  // Clear storage
  private clearStorage() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    
    this.accessToken = null;
    this.refreshToken = null;
    this.user = null;
    this.permissions = [];
  }

  // Login
  public async login(email: string, password: string) {
    try {
      const response = await authAPI.login({ email, password });
      
      if (response.data.success) {
        if (response.data.requires2FA) {
          return {
            success: true,
            requires2FA: true,
            tempToken: response.data.tempToken,
            email: response.data.email,
            message: response.data.message
          };
        }

        const { accessToken, refreshToken, user, permissions } = response.data.data;
        this.saveToStorage(accessToken, refreshToken, user, permissions);
        return { success: true, data: { user, permissions } };
      } else {
        return { success: false, message: response.data.message, code: response.data.code };
      }
    } catch (error: any) {
       return { 
        success: false, 
        message: error.response?.data?.message || 'Giriş yapılamadı',
        code: error.response?.data?.code || 'LOGIN_FAILED'
      };
    }
  }

  // Verify 2FA Code
  public async verify2FA(tempToken: string, code: string) {
    try {
      const response = await authAPI.verify2FA({ tempToken, code });
      
      if (response.data.success) {
        const { accessToken, refreshToken, user, permissions } = response.data.data;
        this.saveToStorage(accessToken, refreshToken, user, permissions);
        return { success: true, data: { user, permissions } };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Doğrulama başarısız'
      };
    }
  }

  // Resend 2FA Code
  public async resend2FA(tempToken: string) {
    try {
      const response = await authAPI.resend2FA({ tempToken });
      return { success: !!response.data.success, message: response.data.message };
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Kod tekrar gönderilemedi'
      };
    }
  }

  // Logout
  public async logout() {
    try {
      if (this.accessToken) {
        await authAPI.logout();
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearStorage();
    }
  }

  // Refresh access token
  public async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshToken) {
      return false;
    }

    try {
      const response = await authAPI.refreshToken({ refreshToken: this.refreshToken });
      
      if (response.data.success) {
        const { accessToken, refreshToken, user, permissions } = response.data.data;
        this.saveToStorage(accessToken, refreshToken, user, permissions);
        return true;
      } else {
        this.clearStorage();
        return false;
      }
    } catch (error: any) {
      console.error('Token refresh error:', error);
      this.clearStorage();
      return false;
    }
  }

  // Get current user
  public getCurrentUser() {
    if (!this.user) {
      const userStr = localStorage.getItem('user');
      if (userStr && userStr !== 'null' && userStr !== 'undefined') {
        try {
          this.user = JSON.parse(userStr);
        } catch (error) {
          this.user = null;
        }
      }
    }
    return this.user;
  }

  // Get access token
  public getAccessToken() {
    // Check localStorage if memory token is null
    if (!this.accessToken) {
      const storedToken = localStorage.getItem('accessToken');
      if (storedToken && storedToken !== 'null' && storedToken !== 'undefined') {
        this.accessToken = storedToken;
      }
    }
    
   
    
    return this.accessToken;
  }

  // Get refresh token
  public getRefreshToken() {
    return this.refreshToken;
  }

  // Get permissions
  public getPermissions() {
    return this.permissions;
  }

  // Check if user is authenticated
  public isAuthenticated(): boolean {
    // Check memory first, then localStorage
    const token = this.accessToken || localStorage.getItem('accessToken');
    const user = this.user || localStorage.getItem('user');

    
    return !!(token && user && token !== 'null' && token !== 'undefined');
  }

  // Helper to safely parse Base64URL JWT payload
  private parseJwtPayload(token: string): any {
    if (!token) return null;
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const base64Url = parts[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      try {
        const parts = token.split('.');
        const base64Url = parts[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(atob(base64));
      } catch (_) {
        return null;
      }
    }
  }

  // Check if access token is expired
  public isAccessTokenExpired(): boolean {
    const token = this.accessToken || localStorage.getItem('accessToken');
    if (!token) return true;
    
    try {
      const payload = this.parseJwtPayload(token);
      if (!payload || !payload.exp) return false;
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      return false;
    }
  }

  // Check if refresh token is expired
  public isRefreshTokenExpired(): boolean {
    const token = this.refreshToken || localStorage.getItem('refreshToken');
    if (!token) return true;
    
    try {
      const payload = this.parseJwtPayload(token);
      if (!payload || !payload.exp) return false;
      const currentTime = Date.now() / 1000;
      return payload.exp < currentTime;
    } catch (error) {
      return false;
    }
  }

  // Auto-refresh token if needed
  public async ensureValidToken(): Promise<boolean> {
    if (!this.isAccessTokenExpired()) {
      return true; // Token is still valid
    }

    if (this.isRefreshTokenExpired()) {
      this.clearStorage();
      return false; // Both tokens expired
    }

    return await this.refreshAccessToken();
  }

  // Check if user has specific role
  public hasRole(role: string): boolean {
    return this.user?.role === role;
  }

  // Check if user has any of the specified roles
  public hasAnyRole(roles: string[]): boolean {
    return roles.includes(this.user?.role);
  }

  // Check if user has specific permission
  public hasPermission(permission: string): boolean {
    return this.permissions.includes(permission);
  }

  // Check if user has any of the specified permissions
  public hasAnyPermission(permissions: string[]): boolean {
    return permissions.some(permission => this.permissions.includes(permission));
  }

  // Check if user has all of the specified permissions
  public hasAllPermissions(permissions: string[]): boolean {
    return permissions.every(permission => this.permissions.includes(permission));
  }

  // Check if user is admin
  public isAdmin(): boolean {
    return this.hasRole('admin');
  }

  // Check if user is supervisor
  public isSupervisor(): boolean {
    return this.hasRole('supervisor');
  }

  // Check if user is agent
  public isAgent(): boolean {
    return this.hasRole('agent');
  }

  // Get user location ID
  public getUserLocationId(): string | null {
    return this.user?.locationId || null;
  }

  // Get user queue IDs
  public getUserQueueIds(): string[] {
    return this.user?.queueIds || [];
  }

  // Check if user can access location
  public canAccessLocation(locationId: string): boolean {
    // Admin can access all locations
    if (this.isAdmin()) {
      return true;
    }
    
    // Supervisor and agent can only access their own location
    return this.getUserLocationId() === locationId;
  }

  // Check if user can access queue
  public canAccessQueue(queueId: string): boolean {
    // Admin can access all queues
    if (this.isAdmin()) {
      return true;
    }
    
    // Supervisor can access queues in their location
    if (this.isSupervisor()) {
      return true; // Will be filtered by location in API
    }
    
    // Agent can only access assigned queues
    return this.getUserQueueIds().includes(queueId);
  }

  // Get current user from API
  public async getCurrentUserFromAPI() {
    try {
      const response = await authAPI.getCurrentUser();
      
      if (response.data?.success) {
        const { user, permissions } = response.data.data;
        this.user = user;
        this.permissions = permissions;
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('permissions', JSON.stringify(permissions));
        return { success: true, user, permissions };
      } else {
        return { success: false, message: response.data?.message };
      }
    } catch (error: any) {
      console.warn('⚠️ Get current user from API warning:', error?.message);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Kullanıcı bilgisi alınamadı' 
      };
    }
  }

  // Update user in storage
  public updateUser(user: any) {
    this.user = user;
    localStorage.setItem('user', JSON.stringify(user));
  }

  // Get user display name
  public getUserDisplayName(): string {
    return this.user?.fullName || `${this.user?.firstName || ''} ${this.user?.lastName || ''}`.trim() || 'Kullanıcı';
  }

  // Get user email
  public getUserEmail(): string {
    return this.user?.email || '';
  }

  // Get user role display name
  public getUserRoleDisplayName(): string {
    const roleMap: { [key: string]: string } = {
      admin: 'Yönetici',
      supervisor: 'Süpervizör',
      agent: 'Temsilci'
    };
    
    return roleMap[this.user?.role] || 'Bilinmeyen';
  }

  // Verify token
  public async verifyToken() {
    try {
      const response = await authAPI.verifyToken();
      
      if (response.data.success) {
        return { success: true, data: response.data.data };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error: any) {
      console.error('Token verification error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Token doğrulanamadı' 
      };
    }
  }

  // Change password
  public async changePassword(currentPassword: string, newPassword: string) {
    try {
      const response = await authAPI.changePassword({ currentPassword, newPassword });
      
      if (response.data.success) {
        return { success: true, message: response.data.message };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error: any) {
      console.error('Change password error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Şifre değiştirilemedi' 
      };
    }
  }

  // Revoke all tokens
  public async revokeAllTokens() {
    try {
      const response = await authAPI.revokeAllTokens();
      
      if (response.data.success) {
        this.clearStorage();
        return { success: true, message: response.data.message };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error: any) {
      console.error('Revoke tokens error:', error);
      this.clearStorage();
      return { 
        success: false, 
        message: error.response?.data?.message || 'Tokenlar iptal edilemedi' 
      };
    }
  }

  // Get user permissions from API
  public async getUserPermissions() {
    try {
      const response = await authAPI.getUserPermissions();
      
      if (response.data.success) {
        const { permissions, role } = response.data.data;
        this.permissions = permissions;
        localStorage.setItem('permissions', JSON.stringify(permissions));
        return { success: true, permissions, role };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error: any) {
      console.error('Get permissions error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'İzinler alınamadı' 
      };
    }
  }

  // Signup
  public async signup(formData: any) {
    try {
      const response = await authAPI.signup(formData);
      return response.data;
    } catch (error: any) {
      // If axios error with response, return its data so caller can handle validation messages
      if (error.response && error.response.data) {
        return error.response.data;
      }
      throw error;
    }
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService;

